import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { CallPreset } from '@goaltree/backend/convex/lib/callPresets'
import {
  type CoachShowRequest,
  type CoachShowResult,
  SHOW_IN_APP_TOOL,
  SHOW_TURNED_OFF,
  SHOW_WAIT_MS,
  coachShowQuiet,
  parseCoachShow,
  waitUntil,
} from '@goaltree/backend/convex/lib/coachShow'
import {
  CALL_PACE_TOOL,
  QUICK_CALL_INSTRUCTIONS,
  callSpeed,
} from '@goaltree/backend/convex/lib/coachToolDefs'
import {
  CALL_CLIENT_TOOLS,
  CAPTURE_CHECKIN_TOOL,
  CHECKIN_SIGHT,
  type CheckinCaptureResult,
  END_FOCUS_TOOL,
  FOCUS_ASK_INSTRUCTIONS,
  FOCUS_DEFAULT_MINUTES,
  FOCUS_RESUME_MAX_GAP_MS,
  type FocusPlan,
  READ_TODAY_TOOL,
  START_FOCUS_TOOL,
  capFocusMinutes,
  checkinCameraLine,
  focusCheckInCue,
  focusCheckIns,
  focusInstructions,
  focusStartLine,
  focusWrapUpCue,
  forceMessage,
  isAck,
  lowMinutesLine,
  todayListLine,
} from '@goaltree/backend/convex/lib/voiceSession'
import { useAction, useConvex, useMutation } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { useEffect, useRef, useState } from 'react'
import type { CameraSetting, SimliClient } from 'simli-client'

import { localDateKey } from '@/hooks/use-coach-chat'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { getSelfCamera, subscribeSelfCamera } from '@/lib/self-camera'

export type VoiceCallStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error'
export type VoiceCallPhase = 'listening' | 'thinking' | 'speaking'

// A single JSON-schema function tool the voice agent may call.
export type VoiceTool = {
  type: 'function'
  name: string
  description: string
  parameters: Record<string, unknown>
}

type CallSession = FunctionReturnType<typeof api.voiceCall.createSession>
export type VoiceSessionApi = {
  create: (args: { locale: string; localDate: string }) => Promise<CallSession>
  resume: () => Promise<{ token: string; remainingSeconds: number }>
  report: (seconds: number) => Promise<unknown>
  face?: () => Promise<{ token: string; faceId: string } | null>
}

type Opts = {
  tools: VoiceTool[]
  onToolCall: (name: string, args: Record<string, unknown>) => Promise<unknown>
  // Called when a spoken turn is transcribed, so callers can persist it. atMs is
  // when the turn started (so a late-arriving transcript still sorts correctly);
  // itemId identifies the turn so refinements upsert in place instead of dupe.
  onTranscript?: (role: 'user' | 'assistant', text: string, atMs?: number, itemId?: string) => void
  // Fired once when a call that went live ends, however it ends.
  onEnded?: (call: { startedAt: number; seconds: number }) => void
  // Realistic coach (has a Simli face) and the user's saved video-on pref.
  realistic?: boolean
  video?: boolean
  // The user's saved quick-call pref.
  quick?: boolean
  // When false, show_in_app tells the coach the user turned it off.
  openScreens?: boolean
  // The onboarding intake: its own free, capped session and only the tools passed in.
  intake?: boolean
  // Replaces the intake's session calls (the signed-out intake has its own).
  sessionApi?: VoiceSessionApi
  // Shrink the call and open the screen. Absent means the call can't navigate.
  onShow?: (req: CoachShowRequest) => Promise<CoachShowResult> | CoachShowResult
  // One camera frame, then the yes/no for a habit photo check-in.
  onCaptureCheckin?: (habitId: string) => Promise<CheckinCaptureResult>
}

const VIDEO_LINES = {
  on: 'The user just turned their video on and can now see your face. Say ONE short, playful line out loud about them seeing your face now — cheeky or self-deprecating about your own looks (like "There I am! Can you see my pretty face now?"), never about their looks.',
  off: 'The user just turned their video off, so they can no longer see your face. Say ONE short, cheeky line out loud about it (like "Wow, lights out!" or "Oh, going camera-shy on me?"), never about their looks.',
}
const VIDEO_LINE_TAIL =
  ' Speak in the language and speaking style of this call, then carry on the conversation where you left off.'
// A quick off→on (or on→off) says nothing until the toggle settles; then only
// the final state's line, and never more than one per VIDEO_LINE_GAP_MS.
const VIDEO_SETTLE_MS = 2000
const VIDEO_LINE_GAP_MS = 15_000
const MAX_RECONNECTS = 3
const DEFAULT_CHARS_PER_S = 18
// How far Simli's face video (and its voice) trails the audio we hand it.
const FACE_LAG_S = 0.3
const LOW_MINUTES_WARN_S = 60

// Something said once nobody is talking: a scripted line, or a one-turn cue.
// Focus cues are dropped when the session ends.
type Cue = ({ say: string } | { instructions: string }) & { focus?: boolean }

type FocusRun = FocusPlan & {
  capped: boolean
  doneMs: number
  runningSince: number | null
  checkIns: number[]
}
// During Focus the realtime line is only open around the coach's lines:
// 'parked' between them (nothing billed, mic closed), 'waking' while it
// reconnects.
export type FocusLine = 'open' | 'parked' | 'waking'
export type FocusView = {
  minutes: number
  task?: string
  leftSeconds: number
  paused: boolean
  capped: boolean
  line: FocusLine
  // Seconds of focus until the next check-in (or the wrap-up when last).
  nextInSeconds: number
  nextIsWrapUp: boolean
}
type WakeReason = 'checkin' | 'wrap' | 'talk'
// How long the line stays open after the coach finishes before parking.
const PARK_AFTER_START_MS = 2500
const PARK_AFTER_CHECKIN_MS = 8000
const PARK_AFTER_TALK_MS = 10_000
const PARK_AFTER_ACK_MS = 1500
const UNREACHABLE_MS = 6000

const focusDoneMs = (f: FocusRun) => f.doneMs + (f.runningSince ? Date.now() - f.runningSince : 0)
const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

function rms(analyser: AnalyserNode | null, buf: Float32Array<ArrayBuffer>) {
  if (!analyser) return 0
  analyser.getFloatTimeDomainData(buf)
  let sum = 0
  for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
  return Math.min(1, Math.sqrt(sum / buf.length) * 5)
}

function pcm16ToBase64(f32: Float32Array): string {
  const buf = new ArrayBuffer(f32.length * 2)
  const view = new DataView(buf)
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function base64ToFloat32(b64: string): Float32Array {
  const bytes = base64ToBytes(b64)
  const view = new DataView(bytes.buffer)
  const n = Math.floor(bytes.length / 2)
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const s = view.getInt16(i * 2, true)
    out[i] = s < 0 ? s / 0x8000 : s / 0x7fff
  }
  return out
}

// Live speech-to-speech "call" with the coach via the xAI realtime voice agent.
// Stopping Simli rejects with "Client initiated disconnect" (LiveKit); that's expected
// when we hang up or drop the face, so it mustn't surface as an unhandled rejection.
function stopSimli(simli: { stop: () => unknown } | null) {
  if (!simli) return
  void Promise.resolve(simli.stop()).catch(() => {})
}

export function useVoiceCall({
  tools,
  onToolCall,
  onTranscript,
  onEnded,
  realistic = false,
  video = true,
  quick = false,
  openScreens = true,
  intake = false,
  sessionApi,
  onShow,
  onCaptureCheckin,
}: Opts) {
  const convex = useConvex()
  const callSession = useAction(api.voiceCall.createSession)
  const intakeSession = useAction(api.intake.createSession)
  const resumeToken = useAction(intake ? api.intake.resumeToken : api.voiceCall.resumeToken)
  const simliSession = useAction(intake ? api.intake.simliSession : api.voiceCall.simliSession)
  const reportUsage = useMutation(intake ? api.intake.reportUsage : api.voiceCall.reportCallUsage)
  const setPrefs = useUpdatePreferences()
  const sessionApiRef = useRef(sessionApi)
  sessionApiRef.current = sessionApi
  const resume = () => sessionApiRef.current?.resume() ?? resumeToken({})
  const faceSession = () => {
    const custom = sessionApiRef.current
    if (!custom) return simliSession({})
    return custom.face?.() ?? Promise.resolve(null)
  }

  const [status, setStatus] = useState<VoiceCallStatus>('idle')
  const [agentSpeaking, setAgentSpeaking] = useState(false)
  const [muted, setMuted] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)
  const [responsePending, setResponsePending] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [toolsRunning, setToolsRunning] = useState(0)
  const [lastSpeaker, setLastSpeaker] = useState<'user' | 'coach' | null>(null)
  const [userCaption, setUserCaption] = useState<{ text: string; final: boolean } | null>(null)
  const [coachCaption, setCoachCaption] = useState<{ text: string; chars: number } | null>(null)

  // Realistic avatar (as vaboo): the coach's voice goes to a Simli live face,
  // which plays it lip-synced. 'failed' falls back to playing it ourselves.
  const [faceStatus, setFaceStatus] = useState<'off' | 'connecting' | 'live' | 'failed'>('off')
  const simliRef = useRef<SimliClient | null>(null)
  const faceStateRef = useRef<'off' | 'connecting' | 'live' | 'failed'>('off')
  const facePendingRef = useRef<Uint8Array[]>([])
  const faceVideoRef = useRef<HTMLVideoElement | null>(null)
  if (!faceVideoRef.current && typeof document !== 'undefined') {
    const v = document.createElement('video')
    v.autoplay = true
    v.playsInline = true
    v.muted = true
    faceVideoRef.current = v
  }
  const faceAudioRef = useRef<HTMLAudioElement | null>(null)
  const [videoOn, setVideoOn] = useState(video)
  const videoOnRef = useRef(video)
  const realisticRef = useRef(realistic)
  const videoPrefRef = useRef(video)
  realisticRef.current = realistic
  const [quickOn, setQuickOn] = useState(quick)
  const quickRef = useRef(quick)
  const quickPrefRef = useRef(quick)
  quickPrefRef.current = quick
  // Session instructions before any quick-mode suffix, and the output format,
  // so pace changes can be re-sent mid-call.
  const baseInstructionsRef = useRef('')
  const outFormatRef = useRef({ type: 'audio/pcm', rate: 24_000 })
  videoPrefRef.current = video
  // Face turned on mid-call: the coach keeps talking locally until its queued
  // audio drains, then Simli takes over, so a reply is never cut or doubled.
  const handoffRef = useRef(false)
  const videoLineRef = useRef<{ state: 'on' | 'off'; at: number } | null>(null)
  const lastVideoLineRef = useRef(0)
  const [videoLineTick, setVideoLineTick] = useState(0)
  const [userTalking, setUserTalking] = useState(false)
  const userTalkingRef = useRef(false)
  const agentSpeakingRef = useRef(false)
  const responsePendingRef = useRef(false)
  const openScreensRef = useRef(openScreens)
  const onShowRef = useRef(onShow)
  const onCaptureRef = useRef(onCaptureCheckin)
  userTalkingRef.current = userTalking
  agentSpeakingRef.current = agentSpeaking
  responsePendingRef.current = responsePending
  openScreensRef.current = openScreens
  onShowRef.current = onShow
  onCaptureRef.current = onCaptureCheckin
  const lastUserSpeechRef = useRef(0)
  // Simli takes 16 kHz PCM, so face calls ask Grok for 16 kHz output.
  const outRateRef = useRef<number | null>(null)
  const silentRef = useRef<GainNode | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  // Bumped by every start and end: a start still awaiting its session, mic or audio
  // context sees a newer value after a hang-up and bails instead of going live.
  const attemptRef = useRef(0)
  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())
  const outRef = useRef<GainNode | null>(null)
  const inAnalyserRef = useRef<AnalyserNode | null>(null)
  const outAnalyserRef = useRef<AnalyserNode | null>(null)
  const levelBufRef = useRef(new Float32Array(512))
  // The coach turn being voiced: its transcript and when its audio plays on the
  // AudioContext clock, so captions can highlight in step with playback.
  const coachRef = useRef<{
    itemId: string
    text: string
    start: number
    end: number
    cut: boolean
    // All of this turn's audio has arrived, so start..end is its full length.
    done: boolean
  } | null>(null)
  // Characters per second the coach speaks at, measured from finished turns.
  const speechRateRef = useRef(DEFAULT_CHARS_PER_S)
  const statusRef = useRef<VoiceCallStatus>('idle')
  const sessionRef = useRef<Awaited<ReturnType<typeof callSession>> | null>(null)
  // Session resumption: the server's conversation id, reconnects used, and when
  // the clock froze for the current drop (the gap isn't billed).
  const conversationIdRef = useRef<string | null>(null)
  const reconnectsRef = useRef(0)
  const pausedAtRef = useRef(0)
  const resumedRef = useRef(false)
  const cuesRef = useRef<Cue[]>([])
  const [cueTick, setCueTick] = useState(0)
  const lowWarnedRef = useRef(false)
  const pendingFocusRef = useRef<FocusPlan | null>(null)
  const askFocusRef = useRef(false)
  const focusRef = useRef<FocusRun | null>(null)
  const [focus, setFocus] = useState<FocusView | null>(null)
  const lineRef = useRef<FocusLine>('open')
  const parkedAtRef = useRef(0)
  const parkDelayRef = useRef(PARK_AFTER_START_MS)
  const wakeReasonRef = useRef<WakeReason>('talk')
  const wakeTriesRef = useRef(0)
  const [focusUnreachable, setFocusUnreachable] = useState(false)
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const rateRef = useRef(24_000)
  const startedAtRef = useRef(0)
  // Last text emitted per item_id — skips re-emitting an unchanged transcript,
  // while still letting a longer (refined) version through so it upgrades the
  // upserted row instead of being locked out.
  const lastEmittedRef = useRef<Map<string, string>>(new Map())
  // Latest (cumulative) transcript text per item_id.
  const transcriptBufRef = useRef<Map<string, string>>(new Map())
  // item_ids of the user's in-progress speech turns, flushed on call end.
  const pendingUserItemsRef = useRef<Set<string>>(new Set())
  // When each user turn started, so a transcript saved late still sorts right.
  const itemStartRef = useRef<Map<string, number>>(new Map())
  // Debounce timers: save a turn once its transcription stops updating (handles
  // long turns whose transcription finishes after the reply already started).
  const flushTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const playCursorRef = useRef(0)
  const mutedRef = useRef(false)
  const startTimeRef = useRef(0)
  const lastReportedRef = useRef(0)
  const remainingAtStartRef = useRef(Infinity)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const toolsRef = useRef(tools)
  const onToolCallRef = useRef(onToolCall)
  const onTranscriptRef = useRef(onTranscript)
  const onEndedRef = useRef(onEnded)
  toolsRef.current = tools
  onToolCallRef.current = onToolCall
  onTranscriptRef.current = onTranscript
  onEndedRef.current = onEnded
  statusRef.current = status

  // Captions follow the audio timeline; the face's WebRTC voice trails the
  // (silent) local schedule by FACE_LAG_S.
  function spokenChars() {
    const c = coachRef.current
    const ctx = ctxRef.current
    if (!c) return 0
    if (c.cut || !ctx || c.end <= c.start) return c.cut ? c.text.length : 0
    const lag = faceStateRef.current === 'live' && !handoffRef.current ? FACE_LAG_S : 0
    const elapsed = Math.min(c.end - c.start, Math.max(0, ctx.currentTime - lag - c.start))
    // Mid-turn the text can be far ahead of the audio received so far, so its
    // per-second share is capped at a little over the coach's measured pace.
    const perSec = c.text.length / (c.end - c.start)
    const chars =
      elapsed *
      (c.done
        ? perSec
        : Math.min(perSec, Math.max(speechRateRef.current, DEFAULT_CHARS_PER_S) * 1.15))
    return Math.min(c.text.length, Math.round(chars))
  }

  // Without an item id (response.done) it closes whichever turn is playing.
  function coachAudioDone(itemId?: string) {
    const c = coachRef.current
    if (!c || c.cut || c.done || (itemId && c.itemId !== itemId)) return
    c.done = true
    const secs = c.end - c.start
    if (secs > 1.5 && c.text.length > 20)
      speechRateRef.current = speechRateRef.current * 0.5 + (c.text.length / secs) * 0.5
  }

  function coachItem(itemId: string) {
    if (coachRef.current?.itemId !== itemId)
      coachRef.current = { itemId, text: '', start: 0, end: 0, cut: false, done: false }
    return coachRef.current
  }

  // Mic and coach loudness (0..1), read each animation frame by the call view.
  function levels() {
    return {
      input: mutedRef.current ? 0 : rms(inAnalyserRef.current, levelBufRef.current),
      output: rms(outAnalyserRef.current, levelBufRef.current),
    }
  }

  function flushPlayback() {
    try {
      simliRef.current?.ClearBuffer()
    } catch {
      // not connected
    }
    facePendingRef.current = []
    for (const src of sourcesRef.current) {
      try {
        src.stop()
      } catch {
        // already stopped
      }
    }
    sourcesRef.current.clear()
    if (ctxRef.current) playCursorRef.current = ctxRef.current.currentTime
    // Freeze the caption at what was actually heard.
    const c = coachRef.current
    if (c && !c.cut) {
      c.text = c.text.slice(0, spokenChars()).trimEnd()
      c.cut = true
      setCoachCaption({ text: c.text, chars: c.text.length })
    }
    markAgentSpeaking(false)
  }

  function reportDelta() {
    const delta = Math.round(elapsedNow() - lastReportedRef.current)
    if (delta >= 1) {
      lastReportedRef.current += delta
      void (sessionApiRef.current?.report(delta) ?? reportUsage({ seconds: delta })).catch(() => {})
    }
  }

  function elapsedNow() {
    return startTimeRef.current
      ? ((pausedAtRef.current || Date.now()) - startTimeRef.current) / 1000
      : 0
  }

  function cleanup() {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    flushPlayback()
    closeMic()
    outRef.current = null
    outAnalyserRef.current = null
    if (ctxRef.current && ctxRef.current.state !== 'closed')
      void ctxRef.current.close().catch(() => {})
    ctxRef.current = null
    if (wsRef.current && wsRef.current.readyState <= 1) wsRef.current.close()
    wsRef.current = null
    try {
      stopSimli(simliRef.current)
    } catch {
      // already closed
    }
    simliRef.current = null
    faceStateRef.current = 'off'
    facePendingRef.current = []
    handoffRef.current = false
    videoLineRef.current = null
    outRateRef.current = null
    silentRef.current = null
    setFaceStatus('off')
    markUserTalking(false)
  }

  // Audio scheduled silently while Simli voiced it carries on out loud from here.
  function voiceLocally() {
    const out = outRef.current
    if (!out) return
    for (const src of sourcesRef.current) {
      try {
        src.disconnect()
        src.connect(out)
      } catch {
        // already ended
      }
    }
  }

  // Connect the coach's Simli face. Gives up after 12s (or on refusal/drop) and
  // the call carries on voice-only, replaying anything the face never voiced.
  async function startFace(token: string) {
    const video = faceVideoRef.current
    const audio = faceAudioRef.current
    if (!video || !audio) {
      faceFailed()
      return
    }
    const { SimliClient } = await import('simli-client')
    if (faceStateRef.current !== 'connecting') return
    const simli = new SimliClient(token, video, audio, null, undefined, 'livekit')
    simliRef.current = simli
    const current = () => simliRef.current === simli
    simli.on('startup_error', () => current() && faceFailed())
    simli.on('error', () => {
      if (current() && faceStateRef.current === 'live') faceFailed()
    })
    simli.on('stop', () => {
      if (current() && faceStateRef.current === 'live') faceFailed()
    })
    try {
      const started = simli.start()
      // If the race below is lost, the late start (or our stop) rejects on its own.
      started.catch(() => {})
      await Promise.race([
        started,
        new Promise((_, no) => setTimeout(() => no(new Error('avatar timed out')), 12_000)),
      ])
    } catch {
      if (current()) faceFailed()
      return
    }
    if (simliRef.current !== simli || faceStateRef.current !== 'connecting') return
    faceStateRef.current = 'live'
    for (const chunk of facePendingRef.current) simli.sendAudioData(chunk)
    facePendingRef.current = []
    setFaceStatus('live')
  }

  function faceFailed() {
    if (faceStateRef.current === 'failed' || faceStateRef.current === 'off') return
    faceStateRef.current = 'failed'
    facePendingRef.current = []
    try {
      stopSimli(simliRef.current)
    } catch {
      // already closed
    }
    simliRef.current = null
    handoffRef.current = false
    if (videoLineRef.current?.state === 'on') videoLineRef.current = null
    voiceLocally()
    setFaceStatus('failed')
    videoOnRef.current = false
    setVideoOn(false)
  }

  function stopFace() {
    if (faceStateRef.current === 'off') return
    faceStateRef.current = 'off'
    facePendingRef.current = []
    handoffRef.current = false
    try {
      stopSimli(simliRef.current)
    } catch {
      // already closed
    }
    simliRef.current = null
    voiceLocally()
    setFaceStatus('off')
  }

  // Saved as the user's pref so the next call starts the same way. Mid-call,
  // the face connects or drops while the voice carries on uninterrupted.
  async function toggleVideo() {
    const next = !videoOnRef.current
    videoOnRef.current = next
    setVideoOn(next)
    const saved = intake ? Promise.resolve() : setPrefs({ callVideo: next }).catch(() => {})
    if (statusRef.current === 'live') {
      videoLineRef.current = { state: next ? 'on' : 'off', at: Date.now() }
      setTimeout(() => setVideoLineTick((n) => n + 1), VIDEO_SETTLE_MS)
    }
    if (!next) {
      stopFace()
      return
    }
    if (faceBusy()) return
    // simliSession reads the pref, so it must be saved first.
    await saved
    await connectFace()
  }

  function faceBusy() {
    return (
      !videoOnRef.current ||
      statusRef.current !== 'live' ||
      faceStateRef.current === 'connecting' ||
      faceStateRef.current === 'live'
    )
  }

  async function connectFace() {
    if (faceBusy()) return
    const face = await faceSession().catch(() => null)
    if (faceBusy()) return
    if (!face) {
      videoOnRef.current = false
      setVideoOn(false)
      videoLineRef.current = null
      setFaceStatus('failed')
      return
    }
    handoffRef.current = true
    faceStateRef.current = 'connecting'
    setFaceStatus('connecting')
    void startFace(face.token)
  }

  function end(next: VoiceCallStatus = 'ended') {
    attemptRef.current++
    reportDelta()
    const startedAt = startTimeRef.current ? startedAtRef.current : 0
    const seconds = Math.round(elapsedNow())
    startedAtRef.current = 0
    pausedAtRef.current = 0
    focusRef.current = null
    pendingFocusRef.current = null
    cuesRef.current = []
    setFocus(null)
    lineRef.current = 'open'
    wakeTriesRef.current = 0
    // Save any user turn still buffered when the call ends.
    for (const timer of flushTimerRef.current.values()) clearTimeout(timer)
    flushTimerRef.current.clear()
    flushPendingUser()
    cleanup()
    startTimeRef.current = 0
    markAgentSpeaking(false)
    markPending(false)
    setToolsRunning(0)
    setReconnecting(false)
    setStatus(next)
    statusRef.current = next
    if (startedAt) onEndedRef.current?.({ startedAt, seconds })
  }

  function playDelta(b64: string, itemId?: string) {
    const ctx = ctxRef.current
    if (!ctx) return
    const face = faceStateRef.current
    if (handoffRef.current && face === 'live' && ctx.currentTime >= playCursorRef.current)
      handoffRef.current = false
    const local = handoffRef.current || face === 'off' || face === 'failed'
    if (!local && face === 'live') simliRef.current?.sendAudioData(base64ToBytes(b64))
    else if (!local && face === 'connecting') facePendingRef.current.push(base64ToBytes(b64))
    const f32 = base64ToFloat32(b64)
    if (f32.length === 0) return
    const buffer = ctx.createBuffer(1, f32.length, outRateRef.current ?? ctx.sampleRate)
    buffer.getChannelData(0).set(f32)
    const src = ctx.createBufferSource()
    src.buffer = buffer
    // While Simli voices the coach, still schedule the audio (silently) so the
    // speaking state and karaoke captions keep their timing.
    src.connect(
      local ? (outRef.current ?? ctx.destination) : (silentRef.current ?? ctx.destination),
    )
    const startAt = Math.max(ctx.currentTime, playCursorRef.current)
    src.start(startAt)
    playCursorRef.current = startAt + buffer.duration
    if (itemId) {
      const c = coachItem(itemId)
      if (!c.cut) {
        if (!c.end) c.start = startAt
        c.end = startAt + buffer.duration
      }
    }
    setLastSpeaker('coach')
    markAgentSpeaking(true)
    sourcesRef.current.add(src)
    src.onended = () => {
      sourcesRef.current.delete(src)
      if (sourcesRef.current.size === 0) markAgentSpeaking(false)
    }
  }

  function saveTranscript(role: 'user' | 'assistant', msg: Record<string, unknown>) {
    const itemId = typeof msg.item_id === 'string' ? msg.item_id : undefined
    if (itemId) {
      const timer = flushTimerRef.current.get(itemId)
      if (timer) {
        clearTimeout(timer)
        flushTimerRef.current.delete(itemId)
      }
    }
    const buffered = itemId ? transcriptBufRef.current.get(itemId) : undefined
    const fromEvent = typeof msg.transcript === 'string' ? msg.transcript : ''
    // Prefer whichever is longer — a `.done` event's inline transcript can lag
    // the cumulative buffer, or vice-versa.
    const text = (fromEvent.length >= (buffered ?? '').length ? fromEvent : (buffered ?? '')).trim()
    const atMs = itemId ? itemStartRef.current.get(itemId) : undefined
    if (!text) return
    // Skip only if we've already emitted this exact text for this turn; a longer
    // refinement still goes through and upgrades the row (upsert by callItemId).
    if (itemId) {
      if (lastEmittedRef.current.get(itemId) === text) return
      lastEmittedRef.current.set(itemId, text)
    }
    if (role === 'user' && focusRef.current && isAck(text)) parkDelayRef.current = PARK_AFTER_ACK_MS
    onTranscriptRef.current?.(role, text, atMs, itemId)
  }

  // Buffer the latest transcript for a user turn and (re)arm a debounce so it's
  // saved once transcription stops updating — this catches long turns whose
  // transcription finishes after the coach already started replying.
  function bufferUserTranscript(itemId: string, text: string) {
    if (!itemStartRef.current.has(itemId)) itemStartRef.current.set(itemId, Date.now())
    transcriptBufRef.current.set(itemId, text)
    pendingUserItemsRef.current.add(itemId)
    const existing = flushTimerRef.current.get(itemId)
    if (existing) clearTimeout(existing)
    // Longer window so brief mid-sentence pauses don't split a turn; any partial
    // that does slip through is harmless — a later save upgrades the same row.
    flushTimerRef.current.set(
      itemId,
      setTimeout(() => saveTranscript('user', { item_id: itemId }), 1500),
    )
  }

  // Save any user turns still buffered (e.g. on call end).
  function flushPendingUser() {
    for (const itemId of pendingUserItemsRef.current) {
      saveTranscript('user', { item_id: itemId })
    }
    pendingUserItemsRef.current.clear()
  }

  async function handleEvent(msg: Record<string, unknown>) {
    const type = msg.type as string
    // Toggle in the console with localStorage.voicecall_debug = '1' to see xAI's
    // exact event stream (types + payloads) while a call is running.
    if (typeof localStorage !== 'undefined' && localStorage.getItem('voicecall_debug') === '1') {
      console.debug('[voiceCall]', type, msg)
    }

    // Catch-all for user-speech transcription — matches whatever xAI names the
    // event (`.updated` cumulative, `.delta` incremental, `.completed` final), so
    // a turn is never lost to an event-name mismatch. `transcript` = full text
    // (replace); `delta` = increment (append).
    if (typeof type === 'string' && type.includes('input_audio_transcription')) {
      const itemId = typeof msg.item_id === 'string' ? msg.item_id : 'user'
      const text =
        typeof msg.transcript === 'string'
          ? msg.transcript
          : typeof msg.delta === 'string'
            ? (transcriptBufRef.current.get(itemId) ?? '') + msg.delta
            : (transcriptBufRef.current.get(itemId) ?? '')
      const final = type.endsWith('.completed') || type.endsWith('.done')
      if (text.trim()) setUserCaption({ text: text.trim(), final })
      // A final event saves immediately; otherwise buffer + debounce.
      if (final) {
        transcriptBufRef.current.set(itemId, text)
        if (!itemStartRef.current.has(itemId)) itemStartRef.current.set(itemId, Date.now())
        saveTranscript('user', { item_id: itemId })
      } else {
        bufferUserTranscript(itemId, text)
      }
      return
    }

    if (type === 'conversation.created') {
      const id = (msg.conversation as { id?: unknown } | undefined)?.id
      if (typeof id === 'string') conversationIdRef.current = id
      return
    }
    // A resumed socket replays the earlier turns, which are already saved.
    if (type === 'conversation.item.created' && resumedRef.current) return

    // A user conversation item that carries its transcript inline.
    if (
      type === 'conversation.item.created' ||
      type === 'conversation.item.added' ||
      type === 'conversation.item.done'
    ) {
      const item = msg.item as
        | { id?: string; role?: string; content?: Array<{ transcript?: string }> }
        | undefined
      if (item?.role === 'user' && Array.isArray(item.content)) {
        const t = item.content
          .map((c) => c.transcript ?? '')
          .join('')
          .trim()
        const itemId = item.id ?? 'user'
        if (t) {
          setUserCaption({ text: t, final: type === 'conversation.item.done' })
          if (type === 'conversation.item.done') {
            transcriptBufRef.current.set(itemId, t)
            if (!itemStartRef.current.has(itemId)) itemStartRef.current.set(itemId, Date.now())
            saveTranscript('user', { item_id: itemId })
          } else {
            bufferUserTranscript(itemId, t)
          }
        }
      }
      return
    }

    if (type === 'response.output_audio.delta' && typeof msg.delta === 'string') {
      playDelta(msg.delta, typeof msg.item_id === 'string' ? msg.item_id : undefined)
    } else if (type === 'response.output_audio.done' || type === 'response.audio.done') {
      coachAudioDone(typeof msg.item_id === 'string' ? msg.item_id : undefined)
    } else if (type === 'input_audio_buffer.speech_started') {
      flushPlayback() // barge-in: user started talking, stop the coach
      markUserTalking(true)
      lastUserSpeechRef.current = Date.now()
      setLastSpeaker('user')
      setUserCaption({ text: '', final: false })
    } else if (
      type === 'input_audio_buffer.speech_stopped' ||
      type === 'input_audio_buffer.committed' ||
      type === 'response.created'
    ) {
      if (type !== 'response.created') {
        markUserTalking(false)
        lastUserSpeechRef.current = Date.now()
      }
      markPending(true)
    } else if (type === 'response.done' || type === 'response.cancelled') {
      coachAudioDone()
      markPending(false)
    } else if (
      type === 'response.output_audio_transcript.delta' &&
      typeof msg.delta === 'string' &&
      typeof msg.item_id === 'string'
    ) {
      transcriptBufRef.current.set(
        msg.item_id,
        (transcriptBufRef.current.get(msg.item_id) ?? '') + msg.delta,
      )
      const c = coachItem(msg.item_id)
      if (!c.cut) c.text += msg.delta
    } else if (
      type === 'response.output_audio_transcript.done' ||
      type === 'response.audio_transcript.done'
    ) {
      saveTranscript('assistant', msg)
    } else if (type === 'response.function_call_arguments.done') {
      const call_id = msg.call_id as string
      const name = msg.name as string
      let args: Record<string, unknown> = {}
      try {
        args = JSON.parse((msg.arguments as string) || '{}')
      } catch {
        // leave empty
      }
      let output: unknown
      let say: string | undefined
      setToolsRunning((n) => n + 1)
      try {
        const local = await clientTool(name, args)
        if (local) ({ output, say } = local)
        else output = await onToolCallRef.current(name, args)
      } catch (e) {
        output = { error: e instanceof Error ? e.message : 'failed' }
      } finally {
        setToolsRunning((n) => Math.max(0, n - 1))
      }
      const ws = wsRef.current
      if (ws && ws.readyState === 1) {
        ws.send(
          JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id, output: JSON.stringify(output ?? {}) },
          }),
        )
        // A scripted line is itself the next turn (no response.create).
        ws.send(JSON.stringify(say ? forceMessage(say) : { type: 'response.create' }))
        markPending(true)
      }
    } else if (type === 'error') {
      console.error('[voiceCall] server error', msg)
    }
  }

  function sessionConfig(session: Awaited<ReturnType<typeof callSession>>, rate: number) {
    return {
      voice: session.voice,
      instructions: sessionInstructions(),
      reasoning: { effort: reasoningEffort() },
      turn_detection: { type: 'server_vad' },
      resumption: { enabled: true },
      audio: {
        input: {
          format: { type: 'audio/pcm', rate },
          // Transcribe the user's speech (grok-transcribe) so turns save to
          // history, biased to their language and their own goal/habit names.
          transcription: {
            model: 'grok-transcribe',
            ...(session.languageHint ? { language_hint: session.languageHint } : {}),
            ...(session.keyterms.length ? { keyterms: session.keyterms } : {}),
          },
        },
        output: { format: outFormatRef.current, speed: paceSpeed() },
      },
      replace: session.replace,
      tools: intake
        ? toolsRef.current
        : [...toolsRef.current, CALL_PACE_TOOL, ...CALL_CLIENT_TOOLS, SHOW_IN_APP_TOOL],
    }
  }

  function openSocket(
    session: Awaited<ReturnType<typeof callSession>>,
    rate: number,
    resume: boolean,
  ) {
    const resumeId = resume ? conversationIdRef.current : null
    const ws = new WebSocket(
      `wss://api.x.ai/v1/realtime?model=${session.model}${resumeId ? `&conversation_id=${encodeURIComponent(resumeId)}` : ''}`,
      [`xai-client-secret.${session.token}`],
    )
    wsRef.current = ws
    resumedRef.current = resume
    baseInstructionsRef.current = !resume
      ? session.instructions
      : lineRef.current === 'waking'
        ? `${session.instructions}\nThe line was paused while they focused and just reopened. Don't greet them again.`
        : `${session.instructions}\nThe call dropped for a moment and just reconnected. Don't greet them again; carry on naturally.`
    outFormatRef.current = { type: 'audio/pcm', rate: outRateRef.current ?? rate }

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'session.update', session: sessionConfig(session, rate) }))
      if (resume) {
        if (pausedAtRef.current) {
          startTimeRef.current += Date.now() - pausedAtRef.current
          pausedAtRef.current = 0
        }
        setReconnecting(false)
        if (lineRef.current === 'waking') {
          lineRef.current = 'open'
          wakeTriesRef.current = 0
          syncFocus()
        }
        setCueTick((n) => n + 1)
        return
      }
      startTimeRef.current = Date.now()
      setStatus('live')
      statusRef.current = 'live'
      const plan = pendingFocusRef.current
      pendingFocusRef.current = null
      // Focus starts with its own scripted line; a call the coach placed opens
      // with the server-written greeting; otherwise the coach opens freely.
      const opener = plan ? focusStartLine(session.language, ...beginFocus(plan)) : session.opener
      const askFocus = askFocusRef.current
      askFocusRef.current = false
      ws.send(
        JSON.stringify(
          opener
            ? forceMessage(opener)
            : askFocus
              ? {
                  type: 'response.create',
                  response: { instructions: `${sessionInstructions()}${FOCUS_ASK_INSTRUCTIONS}` },
                }
              : { type: 'response.create' },
        ),
      )
      markPending(true)
      timerRef.current = setInterval(tick, 1000)
    }

    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return
      try {
        void handleEvent(JSON.parse(ev.data))
      } catch {
        // ignore malformed frame
      }
    }
    // Once live, a drop is handled by onclose (resumes the conversation).
    ws.onerror = () => {
      if (!startTimeRef.current) end('error')
    }
    ws.onclose = () => {
      if (wsRef.current !== ws) return
      if (lineRef.current === 'waking') wakeFailed()
      else if (statusRef.current === 'live' && reconnectsRef.current < MAX_RECONNECTS)
        void reconnect(rate)
      else end('ended')
    }
  }

  function tick() {
    if (!pausedAtRef.current && !tickClock()) return
    tickFocus()
  }

  // The call-minutes clock, which only runs while the line is connected.
  // false once the call ran out of minutes and ended.
  function tickClock() {
    const el = elapsedNow()
    setElapsedSeconds(Math.floor(el))
    const rem = remainingAtStartRef.current - el
    setRemainingSeconds(Number.isFinite(rem) ? Math.max(0, Math.floor(rem)) : null)
    if (Math.floor(el) % 15 === 0) reportDelta()
    if (rem <= 0) {
      end('ended')
      return false
    }
    if (
      !intake &&
      !lowWarnedRef.current &&
      rem <= LOW_MINUTES_WARN_S &&
      remainingAtStartRef.current > LOW_MINUTES_WARN_S + 30
    ) {
      lowWarnedRef.current = true
      queueCue({ say: lowMinutesLine(sessionRef.current?.language ?? 'en') })
    }
    return true
  }

  // Close the line while they focus: the socket, the face and the mic go; the
  // focus timer carries on and the call clock stops.
  function park() {
    if (!focusRef.current || lineRef.current !== 'open' || statusRef.current !== 'live') return
    reportDelta()
    pausedAtRef.current = Date.now()
    parkedAtRef.current = Date.now()
    lineRef.current = 'parked'
    const ws = wsRef.current
    wsRef.current = null
    if (ws && ws.readyState <= 1) ws.close()
    flushPlayback()
    markPending(false)
    markUserTalking(false)
    if (videoOnRef.current) {
      videoOnRef.current = false
      setVideoOn(false)
      videoLineRef.current = null
      stopFace()
    }
    closeMic()
    syncFocus()
  }

  // Reopen the line on the same conversation for a check-in, the wrap-up, or
  // because they want to talk. Past xAI's resumption window it starts afresh.
  async function wake(reason: WakeReason) {
    if (lineRef.current !== 'parked' || statusRef.current !== 'live') return
    lineRef.current = 'waking'
    wakeReasonRef.current = reason
    parkDelayRef.current = reason === 'talk' ? PARK_AFTER_TALK_MS : PARK_AFTER_CHECKIN_MS
    syncFocus()
    const attempt = attemptRef.current
    try {
      const [{ token }] = await Promise.all([resume(), openMic()])
      const session = sessionRef.current
      if (attemptRef.current !== attempt || lineRef.current !== 'waking' || !session) return
      if (Date.now() - parkedAtRef.current > FOCUS_RESUME_MAX_GAP_MS)
        conversationIdRef.current = null
      sessionRef.current = { ...session, token }
      openSocket(sessionRef.current, rateRef.current, true)
    } catch {
      if (attemptRef.current === attempt) wakeFailed()
    }
  }

  // One retry, then the focus timer carries on without the coach.
  function wakeFailed() {
    const reason = wakeReasonRef.current
    const ws = wsRef.current
    wsRef.current = null
    if (ws && ws.readyState <= 1) ws.close()
    closeMic()
    lineRef.current = 'parked'
    if (++wakeTriesRef.current < 2) {
      void wake(reason)
      return
    }
    wakeTriesRef.current = 0
    if (reason === 'wrap') {
      end('ended')
      return
    }
    cuesRef.current = cuesRef.current.filter((c) => !c.focus)
    syncFocus()
    setFocusUnreachable(true)
    setTimeout(() => setFocusUnreachable(false), UNREACHABLE_MS)
  }

  // Reopen the socket on the same conversation (xAI replays the earlier turns),
  // with a fresh token; the call clock is frozen meanwhile.
  async function reconnect(rate: number) {
    reconnectsRef.current++
    if (!pausedAtRef.current) pausedAtRef.current = Date.now()
    setReconnecting(true)
    flushPlayback()
    markPending(false)
    const attempt = attemptRef.current
    try {
      await new Promise((r) => setTimeout(r, 500 * reconnectsRef.current))
      const { token } = await resume()
      const session = sessionRef.current
      if (attemptRef.current !== attempt || statusRef.current !== 'live' || !session) return
      sessionRef.current = { ...session, token }
      openSocket(sessionRef.current, rate, true)
    } catch {
      if (attemptRef.current === attempt) end('ended')
    }
  }

  // focus: start the call straight into a Focus session; askFocus: the coach
  // opens by asking for one. quick / video: this call only, the saved pref is untouched.
  // preset: a one-tap call reason, which sets its own pace (so Quick call is off).
  async function start(
    opts: {
      focus?: FocusPlan
      askFocus?: boolean
      quick?: boolean
      video?: boolean
      preset?: CallPreset
    } = {},
  ) {
    if (status === 'connecting' || status === 'live') return
    setStatus('connecting')
    statusRef.current = 'connecting'
    setElapsedSeconds(0)
    setLastSpeaker(null)
    setUserCaption(null)
    setCoachCaption(null)
    coachRef.current = null
    reconnectsRef.current = 0
    pausedAtRef.current = 0
    conversationIdRef.current = null
    cuesRef.current = []
    lowWarnedRef.current = false
    focusRef.current = null
    setFocus(null)
    pendingFocusRef.current = opts.focus ?? null
    askFocusRef.current = !opts.focus && !!opts.askFocus
    startedAtRef.current = Date.now()
    lastReportedRef.current = 0
    lastEmittedRef.current.clear()
    transcriptBufRef.current.clear()
    pendingUserItemsRef.current.clear()
    itemStartRef.current.clear()
    for (const timer of flushTimerRef.current.values()) clearTimeout(timer)
    flushTimerRef.current.clear()
    const attempt = ++attemptRef.current
    const stale = () => attemptRef.current !== attempt
    // Focus sessions start with the video off; the pref is left alone.
    const videoAtStart = (opts.video ?? videoPrefRef.current) && !opts.focus && !opts.askFocus
    videoOnRef.current = videoAtStart
    setVideoOn(videoAtStart)
    quickRef.current = opts.preset ? false : (opts.quick ?? quickPrefRef.current)
    setQuickOn(quickRef.current)
    try {
      const [session, face] = await Promise.all([
        intake
          ? (sessionApiRef.current?.create ?? intakeSession)({
              locale: navigator.language,
              localDate: localDateKey(),
            })
          : callSession({
              locale: navigator.language,
              localDate: localDateKey(),
              preset: opts.preset,
            }),
        realisticRef.current && videoAtStart
          ? faceSession().catch(() => null)
          : Promise.resolve(null),
      ])
      if (stale()) return
      sessionRef.current = session
      // Simli takes 16 kHz, so realistic coaches always get it: video can then
      // be turned on mid-call without renegotiating the voice session.
      if (face || realisticRef.current) outRateRef.current = 16_000
      if (face) {
        faceStateRef.current = 'connecting'
        setFaceStatus('connecting')
      } else {
        videoOnRef.current = false
        setVideoOn(false)
      }
      remainingAtStartRef.current = session.remainingSeconds
      setRemainingSeconds(session.remainingSeconds)

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      if (stale()) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      streamRef.current = stream
      const AudioCtx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new AudioCtx()
      ctxRef.current = ctx
      await ctx.resume().catch(() => {})
      if (stale()) {
        stream.getTracks().forEach((t) => t.stop())
        void ctx.close().catch(() => {})
        if (streamRef.current === stream) streamRef.current = null
        if (ctxRef.current === ctx) ctxRef.current = null
        return
      }

      const out = ctx.createGain()
      const outAnalyser = ctx.createAnalyser()
      outAnalyser.fftSize = 512
      out.connect(outAnalyser)
      out.connect(ctx.destination)
      outRef.current = out
      outAnalyserRef.current = outAnalyser
      const silent = ctx.createGain()
      silent.gain.value = 0
      silent.connect(ctx.destination)
      silentRef.current = silent
      if (face) void startFace(face.token)

      attachMic(stream)
      rateRef.current = ctx.sampleRate
      openSocket(session, ctx.sampleRate, false)
    } catch (e) {
      if (stale()) return
      startedAtRef.current = 0
      cleanup()
      setStatus('error')
      throw e
    }
  }

  // Mic capture → PCM16 → append. Route through a muted gain node so the
  // processor keeps firing without echoing the mic to the speakers.
  function attachMic(stream: MediaStream) {
    const ctx = ctxRef.current
    if (!ctx) return
    streamRef.current = stream
    stream.getAudioTracks().forEach((t) => (t.enabled = !mutedRef.current))
    const source = ctx.createMediaStreamSource(stream)
    micSourceRef.current = source
    const inAnalyser = ctx.createAnalyser()
    inAnalyser.fftSize = 512
    source.connect(inAnalyser)
    inAnalyserRef.current = inAnalyser
    const processor = ctx.createScriptProcessor(4096, 1, 1)
    processorRef.current = processor
    const sink = ctx.createGain()
    sink.gain.value = 0
    processor.onaudioprocess = (e) => {
      if (mutedRef.current || !wsRef.current || wsRef.current.readyState !== 1) return
      const b64 = pcm16ToBase64(e.inputBuffer.getChannelData(0))
      wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }))
    }
    source.connect(processor)
    processor.connect(sink)
    sink.connect(ctx.destination)
  }

  async function openMic() {
    if (streamRef.current) return
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    if (!ctxRef.current || streamRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    attachMic(stream)
  }

  function closeMic() {
    processorRef.current?.disconnect()
    processorRef.current = null
    micSourceRef.current?.disconnect()
    micSourceRef.current = null
    inAnalyserRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }

  // Tap-to-interrupt: stop the coach's audio now and cancel the rest of its reply.
  function interrupt() {
    if (sourcesRef.current.size === 0) return
    flushPlayback()
    const ws = wsRef.current
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: 'response.cancel' }))
    markPending(false)
  }

  function sessionInstructions() {
    if (intake) return baseInstructionsRef.current
    const f = focusRef.current
    return (
      baseInstructionsRef.current +
      CHECKIN_SIGHT +
      checkinCameraLine(getSelfCamera().on) +
      (quickRef.current ? QUICK_CALL_INSTRUCTIONS : '') +
      (f ? focusInstructions(f) : '')
    )
  }

  function paceSpeed() {
    return callSpeed(sessionRef.current?.speed ?? 1, quickRef.current)
  }

  // Quick calls skip reasoning for snappier replies.
  function reasoningEffort() {
    return quickRef.current ? 'none' : 'high'
  }

  // Re-send what pace and focus change (xAI merges partial session.updates).
  function pushSession() {
    const ws = wsRef.current
    if (ws && ws.readyState === 1)
      ws.send(
        JSON.stringify({
          type: 'session.update',
          session: {
            instructions: sessionInstructions(),
            reasoning: { effort: reasoningEffort() },
            audio: { output: { format: outFormatRef.current, speed: paceSpeed() } },
          },
        }),
      )
  }
  const pushSessionRef = useRef(pushSession)
  pushSessionRef.current = pushSession

  // My camera flipping on has to reach the live prompt, or the coach still
  // answers "I can't see you" from the instructions it started with.
  useEffect(() => subscribeSelfCamera(() => pushSessionRef.current()), [])

  // Quick call: faster speech, rapid-fire instructions and no reasoning,
  // switched on the live session and saved for the next call.
  function setQuick(next: boolean) {
    const changed = quickRef.current !== next
    quickRef.current = next
    setQuickOn(next)
    if (!changed) return { quick: next }
    void setPrefs({ callQuick: next }).catch(() => {})
    pushSession()
    return { quick: next }
  }

  function queueCue(cue: Cue) {
    cuesRef.current.push(cue)
    setCueTick((n) => n + 1)
  }

  function syncFocus() {
    const f = focusRef.current
    const done = f ? focusDoneMs(f) / 1000 : 0
    setFocus(
      f && {
        minutes: f.minutes,
        task: f.task,
        leftSeconds: Math.max(0, Math.ceil(f.minutes * 60 - done)),
        paused: !f.runningSince,
        capped: f.capped,
        line: lineRef.current,
        nextInSeconds: Math.max(0, Math.ceil((f.checkIns[0] ?? f.minutes) * 60 - done)),
        nextIsWrapUp: f.checkIns.length === 0,
      },
    )
  }

  // Starts (or restarts) a Focus session capped to the call minutes left.
  function beginFocus(req: FocusPlan): [FocusPlan, boolean] {
    const left = remainingAtStartRef.current - elapsedNow()
    const { minutes, capped } = capFocusMinutes(req.minutes, Number.isFinite(left) ? left : 1e9)
    const plan = { ...req, minutes }
    focusRef.current = {
      ...plan,
      capped,
      doneMs: 0,
      runningSince: Date.now(),
      checkIns: focusCheckIns(minutes),
    }
    cuesRef.current = cuesRef.current.filter((c) => !c.focus)
    parkDelayRef.current = PARK_AFTER_START_MS
    syncFocus()
    pushSession()
    if (videoOnRef.current) {
      videoOnRef.current = false
      setVideoOn(false)
      videoLineRef.current = null
      stopFace()
    }
    return [plan, capped]
  }

  // wrapUp: the coach calls time and asks how it went.
  function finishFocus(wrapUp: boolean) {
    const f = focusRef.current
    if (!f) return 0
    const minutesDone = Math.round(focusDoneMs(f) / 60_000)
    focusRef.current = null
    setFocus(null)
    cuesRef.current = cuesRef.current.filter((c) => !c.focus)
    pushSession()
    if (wrapUp) queueCue({ instructions: focusWrapUpCue(f, minutesDone) })
    // The wrap-up stays on the line until they're done.
    if (lineRef.current === 'parked') void wake('wrap')
    if (videoPrefRef.current && realisticRef.current && !videoOnRef.current) {
      videoOnRef.current = true
      setVideoOn(true)
      void connectFace()
    }
    return minutesDone
  }

  function tickFocus() {
    const f = focusRef.current
    if (!f) return
    const done = focusDoneMs(f) / 60_000
    if (done >= f.minutes) {
      finishFocus(true)
      return
    }
    if (f.checkIns.length && done >= f.checkIns[0]) {
      const mark = f.checkIns.shift() ?? 0
      queueCue({ instructions: focusCheckInCue(f, mark), focus: true })
      if (lineRef.current === 'parked') void wake('checkin')
    }
    syncFocus()
  }

  function startFocus(req: FocusPlan) {
    if (statusRef.current !== 'live') return
    const [plan, capped] = beginFocus(req)
    queueCue({ say: focusStartLine(sessionRef.current?.language ?? 'en', plan, capped) })
  }

  function toggleFocusPause() {
    const f = focusRef.current
    if (!f) return
    if (f.runningSince) {
      f.doneMs += Date.now() - f.runningSince
      f.runningSince = null
    } else f.runningSince = Date.now()
    syncFocus()
  }

  function markPending(v: boolean) {
    responsePendingRef.current = v
    setResponsePending(v)
  }
  function markUserTalking(v: boolean) {
    userTalkingRef.current = v
    setUserTalking(v)
  }
  function markAgentSpeaking(v: boolean) {
    agentSpeakingRef.current = v
    setAgentSpeaking(v)
  }

  // Say the line first, then open once the user and that playback are quiet.
  async function showInApp(args: Record<string, unknown>): Promise<CoachShowResult> {
    if (!openScreensRef.current) return SHOW_TURNED_OFF
    if (statusRef.current !== 'live') return { opened: false, reason: 'no live call' }
    const parsed = parseCoachShow(args)
    if ('error' in parsed) return { opened: false, reason: parsed.error }
    const started = Date.now()
    await waitUntil(
      () =>
        coachShowQuiet({
          live: statusRef.current === 'live',
          userTalking: userTalkingRef.current,
          playing: sourcesRef.current.size > 0 || agentSpeakingRef.current,
          responsePending: responsePendingRef.current,
          waitedMs: Date.now() - started,
        }),
      SHOW_WAIT_MS,
    )
    if (statusRef.current !== 'live') return { opened: false, reason: 'no live call' }
    if (!openScreensRef.current) return SHOW_TURNED_OFF
    return (await onShowRef.current?.(parsed)) ?? { opened: false, reason: 'not available' }
  }

  // Tools the call itself handles: pace, focus, the Today list, and show-in-app.
  async function clientTool(
    name: string,
    args: Record<string, unknown>,
  ): Promise<{ output: unknown; say?: string } | null> {
    const language = sessionRef.current?.language ?? 'en'
    if (name === SHOW_IN_APP_TOOL.name) return { output: await showInApp(args) }
    if (name === CAPTURE_CHECKIN_TOOL.name) {
      const habitId = typeof args.habit_id === 'string' ? args.habit_id : ''
      const output = (await onCaptureRef.current?.(habitId)) ?? {
        ok: false,
        error: 'Camera check-in is not available on this call.',
      }
      return { output }
    }
    if (name === CALL_PACE_TOOL.name) return { output: setQuick(args.quick === true) }
    if (name === START_FOCUS_TOOL.name) {
      const taskId = text(args.task_id)
      const task =
        text(args.task) ??
        (taskId
          ? (
              await convex
                .query(api.voiceCall.nodeBrief, { nodeId: taskId as Id<'nodes'> })
                .catch(() => null)
            )?.title
          : undefined)
      const [plan, capped] = beginFocus({
        minutes: Number(args.minutes) || FOCUS_DEFAULT_MINUTES,
        ...(task ? { task } : {}),
        ...(taskId ? { taskId } : {}),
      })
      return {
        output: { started: true, minutes: plan.minutes, cappedToCallMinutes: capped },
        say: focusStartLine(language, plan, capped),
      }
    }
    if (name === END_FOCUS_TOOL.name) {
      if (!focusRef.current) return { output: { error: 'no focus session running' } }
      return { output: { ended: true, minutesDone: finishFocus(false) } }
    }
    if (name === READ_TODAY_TOOL.name) {
      const today = await convex.query(api.voiceCall.todayList, { localDate: localDateKey() })
      const tasks = [...today.mustDo, ...today.tasks]
      const habits = today.habits.filter((h) => !h.done)
      return {
        output: {
          mustDo: today.mustDo,
          otherTasks: today.tasks,
          habitsDue: habits,
          note: 'The app is reading this list out loud verbatim now; do not repeat it.',
        },
        say: todayListLine(language, {
          tasks: tasks.map((t) => t.title),
          habits: habits.map((h) => h.title),
        }),
      }
    }
    return null
  }

  function toggleMute() {
    const next = !mutedRef.current
    mutedRef.current = next
    setMuted(next)
    streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !next))
  }

  // Karaoke captions: re-read the coach's playback position while it talks.
  useEffect(() => {
    if (!agentSpeaking) return
    const tick = () => {
      const c = coachRef.current
      if (c) setCoachCaption({ text: c.text, chars: spokenChars() })
    }
    tick()
    const id = setInterval(tick, 120)
    return () => {
      clearInterval(id)
      tick()
    }
  }, [agentSpeaking])

  // Video toggled mid-call: once it settles (and for "on", the face shows) and
  // nobody is talking, the coach remarks on it with one playful line.
  useEffect(() => {
    const line = videoLineRef.current
    if (!line || Date.now() - line.at < VIDEO_SETTLE_MS) return
    if (line.state === 'on' && faceStatus !== 'live') return
    if (agentSpeaking || responsePending || toolsRunning > 0 || userTalking) return
    const ws = wsRef.current
    if (!ws || ws.readyState !== 1) return
    videoLineRef.current = null
    if (Date.now() - lastVideoLineRef.current < VIDEO_LINE_GAP_MS) return
    lastVideoLineRef.current = Date.now()
    // A one-turn instructions override (the session's own + this cue).
    ws.send(
      JSON.stringify({
        type: 'response.create',
        response: {
          instructions: `${sessionInstructions()}\n${VIDEO_LINES[line.state]}${VIDEO_LINE_TAIL}`,
        },
      }),
    )
    markPending(true)
  }, [faceStatus, agentSpeaking, responsePending, toolsRunning, userTalking, videoLineTick])

  // Queued lines and cues go out once nobody is talking.
  useEffect(() => {
    if (!cuesRef.current.length) return
    if (agentSpeaking || responsePending || toolsRunning > 0 || userTalking) return
    const ws = wsRef.current
    if (!ws || ws.readyState !== 1) return
    const cue = cuesRef.current.shift()
    if (!cue) return
    ws.send(
      JSON.stringify(
        'say' in cue
          ? forceMessage(cue.say)
          : {
              type: 'response.create',
              response: { instructions: `${sessionInstructions()}${cue.instructions}` },
            },
      ),
    )
    markPending(true)
  }, [agentSpeaking, responsePending, toolsRunning, userTalking, cueTick])

  // During Focus, park the line once the exchange settles.
  const focusOn = !!focus
  const lineOpen = focus?.line === 'open'
  useEffect(() => {
    if (!focusOn || !lineOpen || status !== 'live' || cuesRef.current.length) return
    if (agentSpeaking || responsePending || toolsRunning > 0 || userTalking || reconnecting) return
    const id = setTimeout(park, parkDelayRef.current)
    return () => clearTimeout(id)
  }, [
    focusOn,
    lineOpen,
    status,
    agentSpeaking,
    responsePending,
    toolsRunning,
    userTalking,
    reconnecting,
    cueTick,
  ])

  // Tear down on unmount.
  useEffect(() => () => cleanup(), [])

  // Context from the app (what they opened), added as a user-side note. Only a
  // reply-worthy note asks for a response; the rest wait for the coach's next turn.
  function sendContext(text: string, respond: boolean): boolean {
    const ws = wsRef.current
    if (!ws || ws.readyState !== 1) return false
    ws.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
      }),
    )
    if (respond) {
      ws.send(JSON.stringify({ type: 'response.create' }))
      markPending(true)
    }
    return true
  }

  function setFaceCamera(camera: CameraSetting | null, lerpSeconds = 0) {
    const simli = simliRef.current
    if (!simli || faceStateRef.current !== 'live') return false
    try {
      simli.setCamera(camera, { lerpSeconds })
      return true
    } catch {
      return false
    }
  }

  function setFaceBackground(backgroundId: string | null) {
    const simli = simliRef.current
    if (!simli || faceStateRef.current !== 'live') return false
    try {
      simli.setBackground(backgroundId)
      return true
    } catch {
      return false
    }
  }

  const phase: VoiceCallPhase = agentSpeaking
    ? 'speaking'
    : responsePending || toolsRunning > 0 || status === 'connecting'
      ? 'thinking'
      : 'listening'

  return {
    status,
    phase,
    agentSpeaking,
    muted,
    reconnecting,
    elapsedSeconds,
    remainingSeconds,
    lastSpeaker,
    userCaption,
    coachCaption,
    levels,
    start,
    end,
    interrupt,
    toggleMute,
    faceStatus,
    faceLive: faceStatus === 'live',
    // The live face is (or is about to be) on screen.
    faceShown:
      faceStatus === 'connecting' ||
      faceStatus === 'live' ||
      (status === 'connecting' && realistic && videoOn),
    videoOn,
    toggleVideo,
    quickOn,
    toggleQuick: () => setQuick(!quickRef.current),
    focus,
    startFocus,
    endFocus: () => finishFocus(true),
    toggleFocusPause,
    talk: () => void wake('talk'),
    focusUnreachable,
    sendContext,
    contextState: () => ({
      coachBusy: agentSpeaking || responsePending || toolsRunning > 0 || reconnecting,
      userTalking,
      lastUserSpeechAt: lastUserSpeechRef.current,
      quiet: !!focus,
    }),
    faceVideoRef,
    faceAudioRef,
    setFaceCamera,
    setFaceBackground,
  }
}
